import { apiClient } from './apiClient';
import { BankTransaction, ImportResult } from '../types/bankTransaction';

export const bankTransactionService = {
  // Получить транзакции по банковскому счету
  async getTransactionsByBankAccount(
    bankAccountId: string,
    page: number = 0,
    size: number = 20,
    startDate?: string,
    endDate?: string
  ): Promise<{ transactions: BankTransaction[]; totalElements: number; totalPages: number }> {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
    });

    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const response = await apiClient.get(`/bank-transactions/bank-account/${bankAccountId}?${params}`);
    return response.data;
  },

  // Получить все транзакции пользователя
  async getAllTransactions(
    page: number = 0,
    size: number = 20,
    startDate?: string,
    endDate?: string,
    bankAccountId?: string,
    transactionType?: string,
    category?: string,
    searchTerm?: string
  ): Promise<{ transactions: BankTransaction[]; totalElements: number; totalPages: number }> {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
    });

    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (bankAccountId) params.append('bankAccountId', bankAccountId);
    if (transactionType) params.append('transactionType', transactionType);
    if (category) params.append('category', category);
    if (searchTerm) params.append('searchTerm', searchTerm);

    const response = await apiClient.get(`/bank-transactions?${params}`);
    return response.data;
  },

  // Получить транзакции по компании
  async getCompanyTransactions(
    companyId: string,
    page: number = 0,
    size: number = 20,
    startDate?: string,
    endDate?: string,
    bankAccountId?: string,
    transactionType?: string,
    category?: string,
    searchTerm?: string
  ): Promise<{ transactions: BankTransaction[]; totalElements: number; totalPages: number }> {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
    });

    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (bankAccountId) params.append('bankAccountId', bankAccountId);
    if (transactionType) params.append('transactionType', transactionType);
    if (category) params.append('category', category);
    if (searchTerm) params.append('searchTerm', searchTerm);

    const response = await apiClient.get(`/bank-transactions/company/${companyId}?${params}`);
    return response.data;
  },

  // Импорт одиночного файла
  async importTransactions(file: File, bankAccountId?: string): Promise<ImportResult> {
    const formData = new FormData();
    formData.append('file', file);
    if (bankAccountId) {
      formData.append('bankAccountId', bankAccountId);
    }

    const response = await apiClient.post('/bank-transactions/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  },

  // Массовый импорт файлов
  async importBulkTransactions(files: File[], bankAccountId?: string): Promise<ImportResult> {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });
    if (bankAccountId) {
      formData.append('bankAccountId', bankAccountId);
    }

    const response = await apiClient.post('/bank-transactions/import-bulk', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  },

  // Отметить транзакцию как сопоставленную
  async markAsMatched(transactionId: string): Promise<void> {
    await apiClient.put(`/bank-transactions/${transactionId}/match`);
  },

  // Отметить транзакцию как несопоставленную
  async markAsUnmatched(transactionId: string): Promise<void> {
    await apiClient.put(`/bank-transactions/${transactionId}/unmatch`);
  },

  // Удалить транзакцию
  async deleteTransaction(transactionId: string): Promise<void> {
    await apiClient.delete( `/bank-transactions/${transactionId}`);
  },

  // Сопоставить транзакции автоматически
  async matchTransactions(companyId: string): Promise<void> {
    await apiClient.post( `/bank-transactions/match/${companyId}`);
  },

  // Отменить сопоставление транзакции
  async unmatchTransaction(companyId: string, transactionId: string): Promise<void> {
    await apiClient.put( `/bank-transactions/${transactionId}/unmatch`);
  },

  // Ручное сопоставление транзакции с инвойсом
  async manualMatchTransactionWithInvoice(companyId: string, transactionId: string, invoiceId: string): Promise<void> {
    await apiClient.post( `/bank-transactions/${transactionId}/match-invoice`, {
      invoiceId
    });
  },

  // Получить статистику сопоставления
  async getMatchingStats(bankAccountId?: string): Promise<{
    totalTransactions: number;
    matchedTransactions: number;
    pendingPayments: number;
    unmatchedTransactions: number;
    matchingRate: number;
  }> {
    const url = bankAccountId 
      ? `/bank-transactions/matching-stats?bankAccountId=${bankAccountId}`
      : '/bank-transactions/matching-stats';
    
    const response = await apiClient.get(url);
    return response.data;
  }
};